import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface TrialStatus {
  subscribed: boolean;
  plan: string;
  plan_status: 'trial' | 'pro';
  subscription_end: string | null;
  trial_days_used: number;
  trial_expired: boolean;
}

const TRIAL_MAX_DAYS = 7;
const CACHE_KEY = 'freeladash_trial_status';
const REFETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes

const DEFAULT_TRIAL_STATUS: TrialStatus = {
  subscribed: false,
  plan: 'trial',
  plan_status: 'trial',
  subscription_end: null,
  trial_days_used: 0,
  trial_expired: false,
};

/** Read last known trial status from localStorage */
function getCachedTrialStatus(): TrialStatus | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    // Validate basic structure
    if (parsed && typeof parsed.plan_status === 'string') {
      return parsed as TrialStatus;
    }
    return null;
  } catch {
    return null;
  }
}

/** Save trial status to localStorage for fallback */
function cacheTrialStatus(status: TrialStatus): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(status));
  } catch {
    // Silently ignore storage errors
  }
}

export function useTrialStatus() {
  const { user, session } = useAuth();

  return useQuery({
    queryKey: ['trial-status', user?.id],
    queryFn: async (): Promise<TrialStatus> => {
      if (!session?.access_token) {
        return getCachedTrialStatus() || DEFAULT_TRIAL_STATUS;
      }

      try {
        const { data, error } = await supabase.functions.invoke('check-subscription', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (error) {
          console.debug('[useTrialStatus] Edge Function error:', error.message || error.name);
          // Return cached value if available, otherwise default
          return getCachedTrialStatus() || DEFAULT_TRIAL_STATUS;
        }

        const result = data as TrialStatus;
        // Cache successful result for future fallback
        cacheTrialStatus(result);
        return result;
      } catch (err) {
        const error = err as Error;
        console.debug('[useTrialStatus] Network error:', error.message);
        // Return cached value if available, otherwise default
        return getCachedTrialStatus() || DEFAULT_TRIAL_STATUS;
      }
    },
    enabled: !!user?.id && !!session?.access_token,
    staleTime: REFETCH_INTERVAL, // 5 minutes
    refetchInterval: REFETCH_INTERVAL, // 5 minutes
    refetchOnWindowFocus: false, // Don't refetch on tab switch
    retry: 1, // Retry once on failure
    retryDelay: 3000, // Wait 3s before retry
    throwOnError: false,
    initialData: getCachedTrialStatus() || undefined, // Provide initial data from cache to avoid blocking UI
  });
}

export function useRefreshTrialStatus() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      await queryClient.invalidateQueries({ queryKey: ['trial-status', user?.id] });
      await queryClient.invalidateQueries({ queryKey: ['subscription', user?.id] });
      await queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
    },
  });
}

// Helper functions for trial status checks
export function getTrialDaysRemaining(trialDaysUsed: number): number {
  return Math.max(0, TRIAL_MAX_DAYS - trialDaysUsed);
}

export function isLastTrialDay(trialDaysUsed: number): boolean {
  return trialDaysUsed === TRIAL_MAX_DAYS - 1;
}

export function isTrialActive(trialStatus: TrialStatus | undefined): boolean {
  if (!trialStatus) return false;
  return trialStatus.plan_status === 'trial' && !trialStatus.trial_expired;
}

export function isPro(trialStatus: TrialStatus | undefined): boolean {
  if (!trialStatus) return false;
  return trialStatus.plan_status === 'pro' || trialStatus.subscribed;
}

export function isTrialExpired(trialStatus: TrialStatus | undefined): boolean {
  if (!trialStatus) return false;
  return trialStatus.plan_status === 'trial' && trialStatus.trial_expired;
}
